import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Sparkles, MessageCircleQuestion, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

declare global {
  interface Window {
    createLemonSqueezy: () => void;
  }
}

export const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    // Initialize Lemon Squeezy checkout overlay
    window.createLemonSqueezy?.();
  }, []);

  const getCheckoutUrl = () => {
    // Replace this URL with your test product URL from LemonSqueezy
    const baseUrl = "https://yesilhealth.lemonsqueezy.com/buy/d46598ed-7e5a-4850-a20c-34246105bb65";
    const params = new URLSearchParams({
      'embed': '1',
      'media': '0',
      'discount': '0',
      'checkout[email]': user?.email || '',
      'checkout[name]': user?.displayName || '',
      'checkout[custom][user_id]': user?.uid || ''
    });
    return `${baseUrl}?${params.toString()}`;
  };

  const applyCoupon = async () => {
    if (!user) return;
    if (!couponCode) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsApplying(true);
    setCouponError('');

    try {
      // Check if coupon exists and is valid
      const couponDoc = await getDoc(doc(db, 'coupons', couponCode));
      
      if (!couponDoc.exists()) {
        setCouponError('Invalid coupon code');
        return;
      }

      const couponData = couponDoc.data();
      
      if (!couponData.isValid) {
        setCouponError('This coupon code is no longer valid');
        return;
      }

      // Check if user has already used this coupon
      const usedBy = couponData.usedBy || [];
      if (usedBy.includes(user.email)) {
        setCouponError('You have already used this coupon');
        return;
      }

      // Check expiration
      if (couponData.expiresAt && new Date(couponData.expiresAt.toDate()) < new Date()) {
        setCouponError('This coupon code has expired');
        return;
      }

      // Update user's credits
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        setCouponError('User not found');
        return;
      }

      const currentCredits = userDoc.data().credits || 0;
      
      try {
        // Update user credits
        await updateDoc(userRef, {
          credits: currentCredits + couponData.credits
        });

        // Add user email to usedBy array in coupon document
        await updateDoc(doc(db, 'coupons', couponCode), {
          usedBy: [...usedBy, user.email]
        });

        // Show success message and redirect to chat
        alert(`Successfully added ${couponData.credits} credits to your account!`);
        navigate('/chat');

      } catch (error) {
        if (error instanceof Error) {
          setCouponError(`Failed to apply coupon: ${error.message}`);
        } else {
          setCouponError('Failed to apply coupon. Please try again.');
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('permission-denied')) {
          setCouponError('You do not have permission to use this coupon.');
        } else if (error.message.includes('not-found')) {
          setCouponError('Invalid coupon code.');
        } else {
          setCouponError(`Error: ${error.message}`);
        }
      } else {
        setCouponError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1012]">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-gray-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to app</span>
        </button>

        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-100 mb-3">Buy Credits</h1>
            <p className="text-base text-gray-400 max-w-xl mx-auto">
              Access evidence-based medical AI consultations powered by specialized databases and peer-reviewed literature
            </p>
          </div>

          <div className="bg-[#1A1B1E] rounded-2xl border border-gray-800 shadow-sm overflow-hidden">
            <div className="p-8 border-b border-gray-800 bg-gradient-to-r from-[#1d3d39] to-[#1A1B1E]">
              <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-8">
                <div>
                  <h2 className="text-xl font-semibold text-gray-100">60 Credits Package</h2>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-100">$10</span>
                    <span className="text-gray-400">one-time</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-100 mb-4">Models</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        <MessageCircleQuestion className="w-4 h-4 text-[#00B5A1]" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-200">Yesil o1</p>
                        <p className="text-sm text-gray-400">1 credit per consultation</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        <Sparkles className="w-4 h-4 text-[#00B5A1]" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-200">Yesil o1 Pro</p>
                        <p className="text-sm text-gray-400">15 credits per consultation</p>
                        <p className="text-xs text-[#00B5A1] mt-1">Enhanced analysis & better insights</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-100 mb-4">Features</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#00B5A1] flex-shrink-0" />
                      <span className="text-gray-400">Access to 50+ medical specialties</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#00B5A1] flex-shrink-0" />
                      <span className="text-gray-400">Evidence-based responses from medical literature</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#00B5A1] flex-shrink-0" />
                      <span className="text-gray-400">24/7 availability with ~1 min response time</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#00B5A1] flex-shrink-0" />
                      <span className="text-gray-400">Detailed consultation reports</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="fixed md:relative bottom-0 left-0 right-0 p-4 md:p-8 bg-[#1A1B1E] border-t border-gray-800 md:border-t-0">
              <a 
                href={getCheckoutUrl()} 
                className="lemonsqueezy-button block w-full py-4 bg-[#00B5A1] text-white font-medium rounded-xl hover:bg-[#009688] transition-colors text-center"
              >
                Buy Credits
              </a>
              
              <div className="mt-3 p-3 bg-[#232429] border border-[#1a3733] rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-[#00B5A1] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-400">
                    <span className="text-gray-300 font-medium">Important:</span> Do not change the pre-filled email in the checkout form. We use this email to match your payment with your account. If changed, credits may not load automatically and you'll need to contact support.
                  </p>
                </div>
              </div>
              
              <div className="mt-4 flex items-center justify-center gap-2 text-gray-500">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 8.25A1.25 1.25 0 012.25 7h19.5A1.25 1.25 0 0123 8.25v7.5A1.25 1.25 0 0121.75 17H2.25A1.25 1.25 0 011 15.75v-7.5z" />
                </svg>
                <span className="text-sm">Secure payment powered by</span>
                <img src="/lemonsqueezy3.svg" alt="LemonSqueezy" className="h-4 md:h-5" />
              </div>
            </div>
          </div>

          {/* Coupon Code Section */}
          <div className="bg-[#1A1B1E] rounded-2xl border border-gray-800 shadow-sm p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-100 mb-4">Have a coupon code?</h2>
            <div className="flex gap-3">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code"
                className="flex-1 px-4 py-2 bg-[#232429] border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B5A1] focus:border-transparent text-gray-200"
              />
              <button
                onClick={applyCoupon}
                disabled={isApplying}
                className="px-6 py-2 bg-[#00B5A1] text-white font-medium rounded-lg hover:bg-[#009688] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isApplying ? 'Applying...' : 'Apply'}
              </button>
            </div>
            {couponError && (
              <p className="mt-2 text-sm text-red-400">{couponError}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 